/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: QueuesPage.tsx (Queues)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Queues list page with table view, search, pagination, and actions.

 🔧 Notes:
    - Refactored from monolithic file
    - Logic delegated to hooks/
    - API calls moved to api/
    - JSX rendering for columns kept here (must be in .tsx file)

 🔁 Last Updated: 2025-01-15 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Edit, Trash2, Copy } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { useQueueList } from './hooks';
import { deleteQueueById, getQueueDuplicateUrl } from './queuesActions';
import type { QueueResponse } from './types';

const STRATEGY_KEYS: Record<string, string> = {
  'ring-all': 'queues.strategy.ringAll',
  'longest-idle-agent': 'queues.strategy.longestIdleAgent',
  'round-robin': 'queues.strategy.roundRobin',
  'top-down': 'queues.strategy.topDown',
  'least-talk-time': 'queues.strategy.leastTalkTime',
  'least-calls': 'queues.strategy.leastCalls',
  'sequentially-by-agent-order': 'queues.strategy.sequentiallyByAgentOrder',
  'sequentially-by-next-agent-order': 'queues.strategy.sequentiallyByNextAgentOrder',
  'random': 'queues.strategy.random'
};

export default function QueuesPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();

  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; queue: QueueResponse | null }>({
    isOpen: false,
    queue: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useQueueList();

  // Add render functions to columns (JSX must be in .tsx file)
  const columns = useMemo(() => {
    return baseColumns.map((column) => {
      if (column.key === 'name') {
        return {
          ...column,
          render: (_value: unknown, item: QueueResponse) => (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-foreground">{item?.name ?? ''}</span>
            </div>
          )
        };
      }

      if (column.key === 'extension') {
        return {
          ...column,
          render: (_value: unknown, item: QueueResponse) => (
            <span className="text-sm font-mono text-foreground">{item?.extension ?? '—'}</span>
          )
        };
      }

      if (column.key === 'strategy') {
        return {
          ...column,
          render: (_value: unknown, item: QueueResponse) => (
            <span className="text-sm text-muted-foreground capitalize">
              {t(STRATEGY_KEYS[item?.strategy] ?? 'queues.strategy.longestIdleAgent')}
            </span>
          )
        };
      }

      if (column.key === 'maxWaitTime') {
        return {
          ...column,
          render: (_value: unknown, item: QueueResponse) => (
            <span className="text-sm text-muted-foreground">
              {item?.maxWaitTime ?? 0}s {item?.maxWaitTime === 0 ? '(unlimited)' : ''}
            </span>
          )
        };
      }

      if (column.key === 'enabled') {
        return {
          ...column,
          render: (_value: unknown, item: QueueResponse) => (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item?.enabled
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                }`}
            >
              {item?.enabled ? t('common.enabled') : t('common.disabled')}
            </span>
          )
        };
      }

      if (column.key === 'updatedAt') {
        return {
          ...column,
          render: (_value: unknown, item: QueueResponse) => (
            <span className="text-sm text-muted-foreground">
              {item?.updatedAt ? new Date(item.updatedAt).toLocaleString() : '—'}
            </span>
          )
        };
      }

      if (column.key === 'actions') {
        return {
          ...column,
          render: (_value: unknown, item: QueueResponse) => renderRowActions(item)
        };
      }

      return column;
    });
  }, [baseColumns, t]);

  const handleCreate = useCallback(() => {
    navigate('/pbx/call-center/queues/new');
  }, [navigate]);

  const handleEdit = useCallback((item: QueueResponse) => {
    navigate(`/pbx/call-center/queues/${item.uuid || item.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback((item: QueueResponse) => {
    navigate(getQueueDuplicateUrl(item.uuid || item.id));
  }, [navigate]);

  const handleDeleteClick = useCallback((item: QueueResponse) => {
    setDeleteModal({ isOpen: true, queue: item });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.queue) {
      return;
    }

    setIsDeleting(true);
    try {
      const idToUse = deleteModal.queue.uuid ?? deleteModal.queue.id;
      await deleteQueueById(idToUse);

      showSuccess(t('queues.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, queue: null });
      refresh();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('queues.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.queue, refresh, showError, showSuccess, t]);

  const renderRowActions = useMemo(() => (item: QueueResponse) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common.edit')} position="top">
        <button
          onClick={() => handleEdit(item)}
          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.duplicate')} position="top">
        <button
          onClick={() => handleDuplicate(item)}
          className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.delete')} position="top">
        <button
          onClick={() => handleDeleteClick(item)}
          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </button>
      </SimpleTooltip>
    </div>
  ), [handleDeleteClick, handleDuplicate, handleEdit, t]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onAdd={handleCreate}
          onDuplicate={handleDuplicate}
          onColumnToggle={handleColumnToggle}
          title={t('queues.title')}
          description={t('queues.description')}
          titleIcon={<Phone className="h-5 w-5 text-primary" />}
          addButtonText={t('queues.actions.add')}
          renderActions={renderRowActions}
          emptyMessage={t('queues.messages.emptyMessage')}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, queue: null })}
        onConfirm={handleDeleteConfirm}
        title={t('common.deleteConfirmation')}
        message={t('queues.messages.deleteConfirm')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}
